/* eslint-disable import/order */
import { NextIntlClientProvider } from 'next-intl';
import type { AppProps as DefaultAppProps } from 'next/app';
import Head from 'next/head';
import { useMemo } from 'react';

import '@src/styles/tokens.css';
import '@src/styles/design-tokens.css';
import '@src/styles/fonts.css';
import '@src/styles/globals.css';
import '@src/styles/reset.css';

import { OverlayProvider } from '@react-aria/overlays';
import { ErrorBoundary } from '@sentry/react';
import { SpeedInsights } from '@vercel/speed-insights/next';
import { IntlProvider } from 'react-intl';
import { QueryClientProvider } from 'react-query';

import { BrandTheme } from '@src/components/common/brand-theme';
import { LayoutComponent } from '@src/components/layouts/layout-component';
import { GenericError } from '@src/components/organisms/generic-error';
import { SEGMENT_ID_APP, SEGMENT_ID_WEB } from '@src/constants/analytics';
import { BrandProvider } from '@src/contexts/brand-context';
import { CmsContentProvider } from '@src/contexts/cms-content-context';
import { FeaturesProvider } from '@src/contexts/features-context';
import { ToastProvider } from '@src/contexts/toast-context';
import { UserAgentProvider } from '@src/contexts/user-agent-context/user-agent-context';
import { WindowProvider } from '@src/contexts/window-context';
import { getFeatureList } from '@src/features';
import { AnalyticsProvider } from '@src/features/analytics/contexts';
import { UserConsentContextProvider } from '@src/features/user-consent/contexts';
import { TripDetailsPage } from '@src/generated/dato-cms';
import { Locale, i18nConfig } from '@src/i18n/locales';
import { BrandData, CommonData, TripsData } from '@src/lib/ssr/types';
import { queryClient } from '@src/query-client';
import { NextRouter, withRouter } from 'next/router';

if (process.env.NEXT_PUBLIC_MOCKS_ACTIVE === 'true') {
  import('@src/mocks');
}

type Props = TripsData &
  BrandData &
  CommonData & { property: TripDetailsPage['property']; unitGroup: TripDetailsPage['unitGroup'] };

type AppProps = DefaultAppProps<Props> & {
  Component: DefaultAppProps['Component'] & {
    Layout?: LayoutComponent<Props>;
  };
} & {
  router: NextRouter;
};

const App = ({ Component, pageProps, router }: AppProps) => {
  const ComponentWithLayout = useMemo(() => {
    return Component.Layout?.(Component) ?? Component;
  }, [Component]);

  const features = getFeatureList({
    isWhiteLabel: pageProps.isWhiteLabel,

    areMemberBenefitsEnabled:
      !!pageProps.cmsContent?.featureToggle?.memberBenefitsProduct && !!pageProps.currentTrip?.hasMemberBenefits,
    npsSurveyCopyAbExperiment: !!pageProps.cmsContent?.featureToggle?.tmNpsSurveyCopyAbExperiment,
    tmNativeSignupModal: !!pageProps.cmsContent?.featureToggle?.tmNativeSignupModal,

    upgradeUpsellEnabled: 'property' in pageProps && !!pageProps.property?.enableRoomUpgrade,

    tmEnableVoucherFakeDoorTest: !!pageProps.cmsContent?.featureToggle?.tmEnableVoucherFakeDoorTest,
  });

  const segmentId = pageProps.isAppWebView ? SEGMENT_ID_APP : SEGMENT_ID_WEB;
  const locale = (router.query.locale as Locale) || i18nConfig.defaultLocale;

  return (
    <>
      <NextIntlClientProvider locale={locale}>
        <IntlProvider
          locale={locale}
          onError={(err) => {
            if (err.code === 'MISSING_TRANSLATION') {
              return;
            }
            throw err;
          }}
        >
          <QueryClientProvider client={queryClient}>
            <UserConsentContextProvider>
              <UserAgentProvider value={{ isAppWebView: pageProps.isAppWebView }}>
                <AnalyticsProvider
                  segmentId={segmentId}
                  brand={pageProps.brand}
                  currentTrip={pageProps.currentTrip}
                  profile={pageProps.profileCache}
                  property={pageProps.property}
                  unitGroup={pageProps.unitGroup}
                >
                  <BrandProvider
                    value={{
                      brand: pageProps.brand,
                      isWhiteLabel: pageProps.isWhiteLabel,
                      isNativeBrand: pageProps.isNativeBrand,
                    }}
                  >
                    {pageProps.isWhiteLabel && <BrandTheme brand={pageProps.brand} />}
                    <Head>
                      <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
                      />
                    </Head>

                    <WindowProvider>
                      <OverlayProvider>
                        <ToastProvider>
                          <ErrorBoundary
                            beforeCapture={(scope) => {
                              scope.setTag('location', 'client-side');
                            }}
                            showDialog
                            fallback={<GenericError />}
                          >
                            <CmsContentProvider value={pageProps.cmsContent}>
                              <FeaturesProvider value={features}>
                                <ComponentWithLayout {...pageProps} />
                              </FeaturesProvider>
                            </CmsContentProvider>
                          </ErrorBoundary>
                        </ToastProvider>
                      </OverlayProvider>
                    </WindowProvider>
                  </BrandProvider>
                </AnalyticsProvider>
              </UserAgentProvider>
            </UserConsentContextProvider>
          </QueryClientProvider>
        </IntlProvider>
      </NextIntlClientProvider>

      <SpeedInsights />
    </>
  );
};
export default withRouter(App);
